import React, { useMemo } from "react";
import { Gauge } from "@phosphor-icons/react";

/**
 * Fatigue Index combining:
 *  - 7d avg RPE (0-10)        weight 0.4
 *  - Volume change vs prev week (% above +20%)  weight 0.3
 *  - Wellness deficit (25 - score)/25          weight 0.3
 *
 * Output 0-100. Higher = more fatigue risk.
 */
export default function FatigueIndex({ sessions, wellnessEntries = [] }) {
  const { value, level, breakdown } = useMemo(() => {
    const now = Date.now();
    const ms7 = 7 * 24 * 3600 * 1000;

    let rpeTotal = 0, rpeCount = 0;
    let volThis = 0, volPrev = 0;
    let distThis = 0, distPrev = 0;
    sessions.forEach((s) => {
      const t = new Date(s.started_at).getTime();
      const inThis = now - t <= ms7;
      const inPrev = now - t > ms7 && now - t <= 2 * ms7;
      (s.sets || []).forEach((st) => {
        const vol = (Number(st.weight_kg) || 0) * (Number(st.reps) || 0);
        const dist = Number(st.distance_m) || 0;
        if (inThis) {
          volThis += vol;
          distThis += dist;
          if (st.rpe != null) { rpeTotal += Number(st.rpe); rpeCount += 1; }
        } else if (inPrev) {
          volPrev += vol;
          distPrev += dist;
        }
      });
    });

    const avgRpe = rpeCount > 0 ? rpeTotal / rpeCount : null;
    const combinedThis = volThis + distThis;
    const combinedPrev = volPrev + distPrev;
    const volChange = combinedPrev > 0 ? (combinedThis - combinedPrev) / combinedPrev : 0;
    const volPenalty = Math.max(0, Math.min(1, (volChange - 0.2) / 0.6));

    const sortedW = [...wellnessEntries].sort((a, b) => b.week_of.localeCompare(a.week_of));
    const latestW = sortedW[0];
    const wellDeficit = latestW ? Math.max(0, (25 - latestW.score) / 25) : null;

    // Weights redistribution if some data missing
    let num = 0, den = 0;
    if (avgRpe != null) { num += 0.4 * (avgRpe / 10); den += 0.4; }
    if (combinedPrev > 0) { num += 0.3 * volPenalty; den += 0.3; }
    if (wellDeficit != null) { num += 0.3 * wellDeficit; den += 0.3; }

    const score = den > 0 ? (num / den) * 100 : null;
    let level = "no_data";
    if (score != null) {
      if (score < 40) level = "optimal";
      else if (score < 60) level = "moderate";
      else if (score < 80) level = "high";
      else level = "critical";
    }
    return {
      value: score,
      level,
      breakdown: {
        avgRpe,
        volThis,
        volPrev,
        distThis,
        distPrev,
        volChangePct: Math.round(volChange * 100),
        wellnessScore: latestW?.score ?? null,
      },
    };
  }, [sessions, wellnessEntries]);

  const config = {
    optimal:  { color: "#32D74B", label: "Óptimo",  msg: "Buena recuperación, podés mantener intensidad." },
    moderate: { color: "#FFD60A", label: "Moderado", msg: "Atención, monitorear carga y sueño." },
    high:     { color: "#FF9500", label: "Alto",     msg: "Recomendado reducir volumen esta semana." },
    critical: { color: "#FF3B30", label: "Crítico",  msg: "Riesgo de sobreentrenamiento. Descarga necesaria." },
    no_data:  { color: "#A3A3A3", label: "Sin datos", msg: "Completá sesiones + wellness para calcular el índice." },
  }[level];

  const pct = value != null ? Math.min(100, Math.max(0, value)) : 0;

  return (
    <div className="card-base p-6" data-testid="fatigue-index">
      <div className="flex items-center gap-2 mb-5">
        <Gauge size={22} weight="duotone" className="text-primary-app" />
        <h3 className="font-display font-black uppercase text-xl tracking-tight">Índice de Fatiga</h3>
      </div>

      <div className="flex items-center gap-6 mb-6 flex-wrap">
        <div>
          <div className="font-display font-black text-[5rem] leading-none tracking-tighter" style={{ color: config.color }}>
            {value != null ? Math.round(value) : "—"}
          </div>
          <div className="text-[10px] uppercase tracking-widest text-muted mt-1">Sobre 100</div>
        </div>
        <div className="flex-1 min-w-[200px]">
          <div className="font-display font-bold uppercase text-2xl tracking-tight mb-1" style={{ color: config.color }}>
            {config.label}
          </div>
          <p className="text-sm text-muted">{config.msg}</p>
        </div>
      </div>

      <div className="h-3 bg-[#262626] overflow-hidden mb-6">
        <div className="h-full transition-all" style={{ width: `${pct}%`, backgroundColor: config.color }} />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-center">
        <div className="bg-[#0A0A0A] border border-app p-3" data-testid="fatigue-rpe">
          <div className="text-[10px] uppercase tracking-widest text-muted mb-1">RPE 7d</div>
          <div className="font-display font-bold text-2xl">
            {breakdown.avgRpe != null ? breakdown.avgRpe.toFixed(1) : "—"}
          </div>
        </div>
        <div className="bg-[#0A0A0A] border border-app p-3" data-testid="fatigue-vol-fuerza">
          <div className="text-[10px] uppercase tracking-widest text-muted mb-1">Vol Fuerza</div>
          <div className="font-display font-bold text-2xl">
            {breakdown.volThis ? `${Math.round(breakdown.volThis).toLocaleString("es-AR")}kg` : "—"}
          </div>
          <div className="text-[9px] text-muted mt-0.5">kg levantados 7d</div>
        </div>
        <div className="bg-[#0A0A0A] border border-app p-3" data-testid="fatigue-vol-aero">
          <div className="text-[10px] uppercase tracking-widest text-muted mb-1">Vol Aeróbico</div>
          <div className="font-display font-bold text-2xl" style={{ color: breakdown.distThis ? "#32D74B" : "#FFFFFF" }}>
            {breakdown.distThis
              ? breakdown.distThis >= 1000
                ? `${(breakdown.distThis / 1000).toFixed(1)}km`
                : `${Math.round(breakdown.distThis)}m`
              : "—"}
          </div>
          <div className="text-[9px] text-muted mt-0.5">distancia 7d</div>
        </div>
        <div className="bg-[#0A0A0A] border border-app p-3">
          <div className="text-[10px] uppercase tracking-widest text-muted mb-1">Δ Carga</div>
          <div className="font-display font-bold text-2xl" style={{ color: breakdown.volChangePct > 30 ? "#FF9500" : "#FFFFFF" }}>
            {(breakdown.volPrev + breakdown.distPrev) > 0 ? `${breakdown.volChangePct >= 0 ? "+" : ""}${breakdown.volChangePct}%` : "—"}
          </div>
        </div>
      </div>
      <div className="mt-3 bg-[#0A0A0A] border border-app p-3 text-center">
        <div className="text-[10px] uppercase tracking-widest text-muted mb-1">Wellness</div>
        <div className="font-display font-bold text-xl">
          {breakdown.wellnessScore != null ? `${breakdown.wellnessScore}/25` : "—"}
        </div>
      </div>
    </div>
  );
}
